import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { BarChart3, Eye, TrendingUp, Heart } from 'lucide-react'

export function Analytics() {
  const { data, isLoading } = useQuery({
    queryKey: ['analytics', 'overview'],
    queryFn: () => api.getAnalyticsOverview(),
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card h-24 animate-pulse bg-slate-100" />
          ))}
        </div>
        <div className="card h-64 animate-pulse bg-slate-100" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Analytics Overview</h2>
        <p className="text-sm text-slate-500">Track your content performance across platforms</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Views"
          value={data?.totalViews?.toLocaleString() || '0'}
          icon={Eye}
          color="blue"
        />
        <StatCard
          title="Engagements"
          value={data?.totalEngagements?.toLocaleString() || '0'}
          icon={Heart}
          color="pink"
        />
        <StatCard
          title="Engagement Rate"
          value={data?.totalViews
            ? `${((data.totalEngagements / data.totalViews) * 100).toFixed(1)}%`
            : '0%'
          }
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Active Platforms"
          value={data?.platformBreakdown
            ? Object.keys(data.platformBreakdown).length.toString()
            : '0'
          }
          icon={BarChart3}
          color="purple"
        />
      </div>

      {/* Platform Breakdown */}
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Platform Breakdown</h3>
        {data?.platformBreakdown && Object.keys(data.platformBreakdown).length > 0 ? (
          <div className="space-y-4">
            {Object.entries(data.platformBreakdown).map(([platform, count]) => {
              const total = Object.values(data.platformBreakdown).reduce((a, b) => a + b, 0)
              const percentage = total > 0 ? (count / total) * 100 : 0

              return (
                <div key={platform}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 capitalize">{platform}</span>
                    <span className="text-sm text-slate-500">{count.toLocaleString()}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-600 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No analytics data yet</p>
            <p className="text-sm text-slate-400">Publish some content to see analytics</p>
          </div>
        )}
      </div>

      {/* Top Content */}
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Top Performing Content</h3>
        {data?.topContent && data.topContent.length > 0 ? (
          <div className="space-y-3">
            {data.topContent.map((item, index) => (
              <div
                key={item.id}
                className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg"
              >
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-primary-700">{index + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{item.title}</p>
                  <p className="text-sm text-slate-500 capitalize">{item.platform}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-slate-900">-</p>
                  <p className="text-xs text-slate-500">views</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No content performance data yet</p>
          </div>
        )}
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string
  value: string
  icon: typeof Eye
  color: 'blue' | 'pink' | 'green' | 'purple'
}) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    pink: 'bg-pink-50 text-pink-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
  }

  return (
    <div className="card">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  )
}
